// useDefectTaskLinks — liest die PT-TSK-Task-Verknüpfungen eines Mangels (defect_task_links, Migr. 302)
// und kapselt das Anlegen einer neuen Verknüpfung über die Edge Function `defect-task-link` (TES-588).
// F-CHK-TSK TES-461 / TES-611. Alles user-JWT-scoped → RLS (team-primär, ADR 0009) ist die Grenze;
// kein service_role. PT-TSK ist ein separates Projekt: `pt_task_id` ist nur eine externe Referenz.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/** Lock-lösende Endzustände — deckungsgleich mit Migr. 304 `_defect_open_task_count`
 *  (nur done/completed zählen NICHT als offen). Single-Source der status_locked-Invariante. */
const TERMINAL_DONE_STATUSES = ["done", "completed"] as const;

export interface DefectTaskLink {
  id: string;
  pt_task_id: string;
  pt_task_status: string | null;
}

const defectTaskLinkKeys = {
  all: ["defect-task-links"] as const,
  byDefect: (defectId: string) => ["defect-task-links", defectId] as const,
};

/** Anzahl noch offener (nicht done/completed) Task-Links — Basis für das status_locked-Banner. */
export function openTaskLinkCount(links: DefectTaskLink[]): number {
  return links.filter(
    (l) =>
      !TERMINAL_DONE_STATUSES.includes(
        (l.pt_task_status ?? "") as (typeof TERMINAL_DONE_STATUSES)[number],
      ),
  ).length;
}

/** Lädt die Task-Links eines Mangels (RLS-gescoped). */
export function useDefectTaskLinks(defectId: string | undefined) {
  const { session } = useAuth();
  return useQuery({
    queryKey: defectTaskLinkKeys.byDefect(defectId ?? ""),
    enabled: Boolean(defectId) && !!session,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<DefectTaskLink[]> => {
      const { data, error } = await supabase
        .from("defect_task_links")
        .select("id, pt_task_id, pt_task_status")
        .eq("defect_id", defectId as string)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });
}

/** Antwort der Edge Function `defect-task-link` (Erfolgsfall). */
interface LinkResponse {
  link_id?: string;
  status_locked?: boolean;
  error?: string;
}

/** Legt eine neue Mangel↔PT-TSK-Verknüpfung an (Edge Function, user-JWT → RLS). */
export function useLinkTaskToDefect(defectId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (ptTaskId: string): Promise<LinkResponse> => {
      const { data, error } = await supabase.functions.invoke("defect-task-link", {
        body: { defect_id: defectId, pt_task_id: ptTaskId },
      });
      if (error) throw error;
      if (!data) throw new Error("empty response");
      if ((data as LinkResponse).error) throw new Error((data as LinkResponse).error);
      return data as LinkResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: defectTaskLinkKeys.byDefect(defectId) });
      toast.success(t("defects.taskLinks.linkSuccess"));
    },
    onError: (err: Error) => {
      toast.error(t("defects.taskLinks.linkError"), { description: err.message });
    },
  });
}

/** Entfernt eine einzelne Verknüpfung (direkter RLS-DELETE, team-scoped). Der DELETE-Trigger (Migr. 304)
 *  rechnet defects.status_locked neu — daher invalidiert der Erfolg die Link-Query und der Lock-Banner
 *  evaluiert neu. F-CHK-TSK TES-616. */
export function useUnlinkTaskFromDefect(defectId: string) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (linkId: string): Promise<void> => {
      const { error } = await supabase.from("defect_task_links").delete().eq("id", linkId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: defectTaskLinkKeys.byDefect(defectId) });
      toast.success(t("defects.taskLinks.unlinkSuccess"));
    },
    onError: (err: Error) => {
      toast.error(t("defects.taskLinks.unlinkError"), { description: err.message });
    },
  });
}
